import UilTrashAlt from '@iconscout/react-unicons/icons/uil-trash-alt';
import ModalPopup from 'features/settings/pvt-components/chatbot-settings/modal-popup';
import { useFormik } from 'formik';
import DropDownEditable from 'pub-components/ui-controls/DropDownEditable';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Tab, Tabs } from 'react-bootstrap';
import dropdownEnums from 'services/dropdown-enums';
import { apiNodeDefaultValues } from 'services/schemas/chatbotSchemas';
import { apiNodeSchema } from 'services/schemas/chatbotSchemas/schemas';
import { flattenObject, objectToRows, rowsToObject } from '../helper';

const ApiNodeSidebar = ({
  open,
  setOpen,
  data,
  onChange,
  onClose,
  pendingTransition,
  setPendingTransition,
  items,
  setItems,
  editable = true
}) => {
  const [response, setResponse] = useState({ status: 0, text: '', data: {} });
  const [showResponse, setShowResponse] = useState(false);
  const [headerKeyErrors, setHeaderKeyErrors] = useState({});
  const [queryParamKeyErrors, setQueryParamKeyErrors] = useState({});
  const [mappingKeyErrors, setMappingKeyErrors] = useState({});
  const skipBodyQueryRowsHydrationRef = useRef(false);
  const EMPTY_OBJECT = {};

  const getInitialValues = () => {
    if (data && Object.keys(data).length > 0) {
      console.log('ApiNodeSidebar: Loading data from prop:', data);
      return { ...apiNodeDefaultValues, ...data };
    }

    // Fallback: default values
    return apiNodeDefaultValues;
  };

  const [initialValues, setInitialValues] = useState(() => getInitialValues());

  const toSafeObject = value =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : EMPTY_OBJECT;

  const getBodyOrQueryField = apiBodyType =>
    apiBodyType === 'json' ? 'apiBodyTemplate' : 'queryParams';

  const getBodyOrQueryDuplicateMessage = apiBodyType =>
    apiBodyType === 'json' ? 'Duplicate body key' : 'Duplicate query parameter';

  const getDuplicateKeyErrors = (rows, duplicateMessage) => {
    const keyIndex = rows.reduce((acc, row) => {
      const normalizedKey = row.key?.trim();
      if (!normalizedKey) return acc;

      acc[normalizedKey] = [...(acc[normalizedKey] || []), row.id];
      return acc;
    }, {});

    return Object.values(keyIndex).reduce((acc, ids) => {
      if (ids.length < 2) return acc;

      ids.forEach(id => {
        acc[id] = duplicateMessage;
      });
      return acc;
    }, {});
  };

  const validateRowsForUniqueKeys = (rows, field, setRowErrors, message) => {
    const duplicateErrors = getDuplicateKeyErrors(rows, message);
    const hasDuplicateKeys = Object.keys(duplicateErrors).length > 0;

    setRowErrors(duplicateErrors);
    formik.setFieldTouched(field, true, false);

    if (hasDuplicateKeys) {
      formik.setFieldError(field, message);
      return false;
    }

    formik.setFieldError(field, undefined);
    return true;
  };

  const handleSubmit = async values => {
    const bodyOrQueryField = getBodyOrQueryField(values.apiBodyType);
    const bodyOrQueryDuplicateMessage = getBodyOrQueryDuplicateMessage(
      values.apiBodyType
    );
    const areHeadersValid = validateRowsForUniqueKeys(
      headerRows,
      'apiHeaders',
      setHeaderKeyErrors,
      'Duplicate header key'
    );
    const areBodyOrQueryValid = validateRowsForUniqueKeys(
      queryParamRows,
      bodyOrQueryField,
      setQueryParamKeyErrors,
      bodyOrQueryDuplicateMessage
    );
    const areMappingsValid = validateRowsForUniqueKeys(
      mappingRows,
      'responseMapping',
      setMappingKeyErrors,
      'Duplicate response field'
    );

    if (!areHeadersValid || !areBodyOrQueryValid || !areMappingsValid) {
      return;
    }

    const normalizedValues = {
      ...values,
      apiHeaders: rowsToObject(headerRows),
      [bodyOrQueryField]: rowsToObject(queryParamRows),
      responseMapping: rowsToObject(mappingRows)
    };
    const errors = await formik.validateForm(normalizedValues);

    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
      return;
    }

    onChange(normalizedValues);
    setOpen(false);
  };

  // Update initial values when data changes
  useEffect(() => {
    const newInitialValues = getInitialValues();
    console.log('ApiNodeSidebar: Updating initial values:', newInitialValues);
    setInitialValues(newInitialValues);

    // Also update row states immediately
    setHeaderRows(objectToRows(toSafeObject(newInitialValues?.apiHeaders)));
    setQueryParamRows(
      objectToRows(
        toSafeObject(
          newInitialValues[getBodyOrQueryField(newInitialValues.apiBodyType)]
        )
      )
    );
    setMappingRows(
      objectToRows(toSafeObject(newInitialValues.responseMapping))
    );
  }, [data]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: apiNodeSchema,
    onSubmit: handleSubmit
  });

  const updateFormikField = (field, value, shouldValidate = true) => {
    const updated = { ...formik.values, [field]: value };
    formik.setFieldValue(field, value, shouldValidate);
    formik.setFieldTouched(field, true, false);
    onChange(updated);
  };

  const syncRowsToFormikField = (rows, field) => {
    updateFormikField(field, rowsToObject(rows), true);
  };

  useEffect(() => {
    if (skipBodyQueryRowsHydrationRef.current) {
      skipBodyQueryRowsHydrationRef.current = false;
      return;
    }

    const sourceField = getBodyOrQueryField(formik.values.apiBodyType);
    setQueryParamRows(objectToRows(toSafeObject(formik.values[sourceField])));
  }, [
    formik.values.apiBodyType,
    formik.values.apiBodyTemplate,
    formik.values.queryParams
  ]);

  const [headerRows, setHeaderRows] = useState(() => {
    const initialValues = getInitialValues();
    return objectToRows(toSafeObject(initialValues.apiHeaders));
  });

  const addHeader = () => {
    setHeaderRows(rows => [
      ...rows,
      { id: crypto.randomUUID(), key: '', value: '' }
    ]);
  };

  const clearAllHeaders = () => {
    setHeaderRows([]);
    setHeaderKeyErrors({});
    handleChange('apiHeaders', {});
  };

  const updateHeader = (id, field, value) => {
    setHeaderRows(rows =>
      rows.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const deleteHeader = id => {
    setHeaderRows(rows => rows.filter(r => r.id !== id));
  };

  const syncHeadersToFormik = () => {
    if (
      !validateRowsForUniqueKeys(
        headerRows,
        'apiHeaders',
        setHeaderKeyErrors,
        'Duplicate header key'
      )
    ) {
      return;
    }

    syncRowsToFormikField(headerRows, 'apiHeaders');
  };

  const [queryParamRows, setQueryParamRows] = useState(() => {
    const initialValues = getInitialValues();
    return objectToRows(
      toSafeObject(
        initialValues[getBodyOrQueryField(initialValues.apiBodyType)]
      )
    );
  });

  const addQueryParams = () => {
    setQueryParamRows(rows => [
      ...rows,
      { id: crypto.randomUUID(), key: '', value: '' }
    ]);
  };

  const clearAllQueryParams = () => {
    setQueryParamRows([]);
    setQueryParamKeyErrors({});
    handleChange(getBodyOrQueryField(formik.values.apiBodyType), {});
  };

  const updateQueryParams = (id, field, value) => {
    setQueryParamRows(rows =>
      rows.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const deleteQueryParams = id => {
    setQueryParamRows(rows => rows.filter(r => r.id !== id));
  };

  const syncBodyOrQueryToFormik = () => {
    const bodyOrQueryField = getBodyOrQueryField(formik.values.apiBodyType);
    const duplicateMessage = getBodyOrQueryDuplicateMessage(
      formik.values.apiBodyType
    );

    if (
      !validateRowsForUniqueKeys(
        queryParamRows,
        bodyOrQueryField,
        setQueryParamKeyErrors,
        duplicateMessage
      )
    ) {
      return;
    }

    syncRowsToFormikField(queryParamRows, bodyOrQueryField);
  };

  const [mappingRows, setMappingRows] = useState(() => {
    const initialValues = getInitialValues();
    return objectToRows(toSafeObject(initialValues.responseMapping));
  });

  const addMapping = () => {
    setMappingRows(rows => [
      ...rows,
      { id: crypto.randomUUID(), key: '', value: '' }
    ]);
  };

  const clearAllMappings = () => {
    setMappingRows([]);
    setMappingKeyErrors({});
    handleChange('responseMapping', {});
  };

  const updateMapping = (id, field, value) => {
    setMappingRows(rows =>
      rows.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const deleteMapping = id => {
    setMappingRows(rows => rows.filter(r => r.id !== id));
  };

  const syncMappingToFormik = () => {
    if (
      !validateRowsForUniqueKeys(
        mappingRows,
        'responseMapping',
        setMappingKeyErrors,
        'Duplicate response field'
      )
    ) {
      return;
    }

    syncRowsToFormikField(mappingRows, 'responseMapping');
  };

  const switchApiBodyType = nextBodyType => {
    const currentValues = formik.values;
    const currentBodyField = getBodyOrQueryField(currentValues.apiBodyType);
    const targetField = getBodyOrQueryField(nextBodyType);
    const activeBodyOrQueryObject = rowsToObject(queryParamRows);
    const valuesWithActiveRows = {
      ...currentValues,
      [currentBodyField]: activeBodyOrQueryObject
    };
    const sourceObject = toSafeObject(valuesWithActiveRows[currentBodyField]);
    const targetObject = toSafeObject(valuesWithActiveRows[targetField]);
    const movedValue =
      Object.keys(sourceObject).length > 0 ? { ...sourceObject } : targetObject;

    const updated = {
      ...valuesWithActiveRows,
      apiBodyType: nextBodyType,
      [targetField]: movedValue,
      [currentBodyField]: {}
    };

    // Keep current editable rows intact when toggling tabs.
    // Objects cannot represent duplicate keys, but row UI needs to preserve them.
    skipBodyQueryRowsHydrationRef.current = true;
    formik.setValues(updated);
    formik.setTouched({
      ...formik.touched,
      apiBodyType: true,
      [targetField]: true,
      [currentBodyField]: true
    });
    formik.setFieldError(currentBodyField, undefined);

    validateRowsForUniqueKeys(
      queryParamRows,
      targetField,
      setQueryParamKeyErrors,
      getBodyOrQueryDuplicateMessage(nextBodyType)
    );

    onChange(updated);
  };

  const handleChange = (field, value) => {
    updateFormikField(field, value, true);
  };

  const handleUrlBlur = e => {
    let key = e.target.id;
    let value = e.target.value;

    if (value && !/^https?:\/\//i.test(value)) {
      value = `https://${value}`;
    }

    formik.setFieldValue(key, value);
    formik.setFieldTouched(key, true, true);
    onChange({ ...formik.values, [key]: value });
  };

  const handleTestRequest = async () => {
    try {
      const url = new URL(formik.values.apiUrl);

      // Append query params
      Object.entries(formik.values.queryParams || {}).forEach(
        ([key, value]) => {
          if (key && value !== undefined && value !== null) {
            url.searchParams.append(key, value);
          }
        }
      );

      const options = {
        method: formik.values.apiMethod,
        headers: { ...(formik.values.apiHeaders || {}) }
      };

      // Attach body for POST / PUT
      if (
        ['POST', 'PUT'].includes(formik.values.apiMethod) &&
        formik.values.apiBodyTemplate
      ) {
        options.body = formik.values.apiBodyTemplate;
        options.headers['Content-Type'] = 'application/json';
      }

      const res = await fetch(url.toString(), options);
      const text = await res.text();

      let parsedData;
      try {
        parsedData = JSON.parse(text);
      } catch {
        parsedData = { response: text };
      }

      // Store raw response
      setResponse({
        status: res.status,
        text,
        data: parsedData
      });
      setShowResponse(true);

      // Auto-generate responseMapping if empty
      if (
        // Object.keys(formik.values.responseMapping || {}).length === 0 &&
        typeof parsedData === 'object' &&
        parsedData !== null
      ) {
        const responseMappings = flattenObject(parsedData, 3);

        handleChange('responseMapping', responseMappings);
      }
    } catch (err) {
      alert(`Test failed: ${err.message}`);
    }
  };

  // Reusable Clear All Button Component
  const ClearAllButton = ({ onClick, disabled }) => (
    <Button
      className="ml-auto"
      variant="outline-danger"
      size="sm"
      onClick={onClick}
      disabled={disabled}
    >
      Clear All
    </Button>
  );

  const sharedRowTextBoxStyles = {
    width: '100%',
    marginRight: '0.5rem',
    marginBottom: '1rem'
  };

  return (
    <>
      <ModalPopup
        open={open}
        handleClose={() => setOpen(false)}
        size="lg"
        title="API Configuration"
        onSave={formik.handleSubmit}
      >
        <div className="d-flex mb-6r">
          {/* {JSON.stringify(formik.isValid)} */}
          <DropDownEditable
            heading="Method"
            field="apiMethod"
            dataProperty={formik.values.apiMethod}
            editable
            handleChange={(e, field, value) => handleChange(field, value)}
            titleText="API Method"
            materialFields
            options={dropdownEnums.enum_apiMethodOptions}
            fullWidth={false}
            className={`col-md-2 mr-2`}
            error={formik.touched.apiMethod && formik.errors.apiMethod}
          />
          <SimpleLabelTextBox
            heading="API URL"
            field="apiUrl"
            dataProperty={formik.values.apiUrl}
            editable={editable}
            handleChange={(e, field, value) => handleChange(field, value)}
            styles={{ width: '100%' }}
            handleBlur={handleUrlBlur}
            error={formik.touched.apiUrl && formik.errors.apiUrl}
          />
        </div>

        <div className="mb-6r">
          <SimpleLabelTextBox
            heading="Timeout (ms)"
            field="timeout"
            dataProperty={formik.values.timeout}
            editable={editable}
            handleChange={(e, field, value) =>
              handleChange(field, parseInt(value || 0))
            }
            type="number"
            error={formik.touched.timeout && formik.errors.timeout}
          />
        </div>

        <div className="d-flex mb-6r">
          <DropDownEditable
            heading="Api body Type"
            field="apiBodyType"
            dataProperty={formik.values.apiBodyType}
            editable={editable}
            handleChange={(e, field, value) => {
              switchApiBodyType(value);
            }}
            titleText="Select tab content type"
            materialFields
            options={dropdownEnums.enum_ApiBodyTypeOptions}
            error={formik.touched.apiBodyType && formik.errors.apiBodyType}
          />
        </div>
        <Tabs defaultActiveKey="headers" className="mb-6r">
          <Tab eventKey="headers" title="Headers">
            {/* {formik.touched.apiHeaders && formik.errors.apiHeaders && (
              <div className="text-danger small mb-2">
                {formik.errors.apiHeaders}
              </div>
            )} */}
            <div className="d-flex gap-2 mb-2">
              <Button variant="secondary" size="sm" onClick={addHeader}>
                + New header
              </Button>
              <ClearAllButton
                onClick={clearAllHeaders}
                disabled={!headerRows.length}
              />
            </div>
            {headerRows.map((row, idx) => (
              <div
                key={row.id}
                className="d-flex gap-2 align-items-center justify-space-between mb-2 mt-3"
              >
                <SimpleLabelTextBox
                  key={`header-${row.id}-key`}
                  heading="Key"
                  field={`header-${row.id}-key`}
                  dataProperty={row.key}
                  error={headerKeyErrors[row.id]}
                  editable={editable}
                  handleChange={(e, field, value) =>
                    updateHeader(row.id, 'key', value)
                  }
                  handleBlur={() => syncHeadersToFormik()}
                  styles={sharedRowTextBoxStyles}
                />
                <SimpleLabelTextBox
                  key={`header-${row.id}-value`}
                  heading="Value"
                  field={`header-${row.id}-value`}
                  dataProperty={row.value}
                  editable={editable}
                  handleChange={(e, field, value) =>
                    updateHeader(row.id, 'value', value)
                  }
                  handleBlur={() => syncHeadersToFormik()}
                  styles={sharedRowTextBoxStyles}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    deleteHeader(row.id);
                    setTimeout(syncHeadersToFormik, 0);
                  }}
                  style={{ marginBottom: '1rem' }}
                >
                  <UilTrashAlt
                    style={{
                      width: '24px',
                      height: '24px'
                    }}
                  />
                </Button>
              </div>
            ))}
          </Tab>

          <Tab
            eventKey="third"
            title={
              formik.values.apiBodyType === 'json' ? 'Body' : 'Query Parameters'
            }
          >
            <>
              <div className="d-flex gap-2 mb-2">
                <Button variant="secondary" size="sm" onClick={addQueryParams}>
                  + New parameter
                </Button>
                <ClearAllButton
                  onClick={clearAllQueryParams}
                  disabled={!queryParamRows.length}
                />
              </div>
              {queryParamRows.map((row, idx) => (
                <div
                  key={row.id}
                  className="d-flex align-items-center gap-2 mb-2 mt-3"
                >
                  <SimpleLabelTextBox
                    key={`query-${row.id}-key`}
                    heading="Key"
                    field={`query-${row.id}-key`}
                    dataProperty={row.key}
                    error={queryParamKeyErrors[row.id]}
                    editable={editable}
                    handleChange={(e, field, value) =>
                      updateQueryParams(row.id, 'key', value)
                    }
                    handleBlur={() => syncBodyOrQueryToFormik()}
                    styles={sharedRowTextBoxStyles}
                  />
                  <SimpleLabelTextBox
                    key={`query-${row.id}-value`}
                    heading="Value"
                    field={`query-${row.id}-value`}
                    dataProperty={row.value}
                    editable={editable}
                    handleChange={(e, field, value) =>
                      updateQueryParams(row.id, 'value', value)
                    }
                    handleBlur={() => syncBodyOrQueryToFormik()}
                    styles={sharedRowTextBoxStyles}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => deleteQueryParams(row.id)}
                    style={{ marginBottom: '1rem' }}
                  >
                    <UilTrashAlt
                      style={{
                        width: '24px',
                        height: '24px'
                        // color: '#dc3545'
                      }}
                    />
                  </Button>
                </div>
              ))}
            </>
          </Tab>

          <Tab eventKey="mapping" title="Response Mapping">
            <p className="small text-muted">
              Map API response fields to workflow variables
            </p>
            <div className="d-flex gap-2 mb-2">
              <Button variant="secondary" size="sm" onClick={addMapping}>
                + Add mapping
              </Button>
              <ClearAllButton
                onClick={clearAllMappings}
                disabled={!mappingRows.length}
              />
            </div>
            {mappingRows.map((row, idx) => (
              <div
                key={row.id}
                className="d-flex gap-2 align-items-center mb-2 mt-3"
              >
                <SimpleLabelTextBox
                  key={`mapping-${row.id}-key`}
                  heading="Response Field"
                  field={`mapping-${row.id}-key`}
                  dataProperty={row.key}
                  error={mappingKeyErrors[row.id]}
                  editable={editable}
                  handleChange={(e, field, value) =>
                    updateMapping(row.id, 'key', value)
                  }
                  handleBlur={() => syncMappingToFormik()}
                  styles={sharedRowTextBoxStyles}
                />
                <span
                  style={{
                    marginRight: '0.5rem',
                    marginBottom: '1rem'
                  }}
                >
                  →
                </span>
                <SimpleLabelTextBox
                  key={`mapping-${row.id}-value`}
                  heading="Variable Name"
                  field={`mapping-${row.id}-value`}
                  dataProperty={row.value}
                  editable={editable}
                  handleChange={(e, field, value) =>
                    updateMapping(row.id, 'value', value)
                  }
                  handleBlur={() => syncMappingToFormik()}
                  styles={sharedRowTextBoxStyles}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => deleteMapping(row.id)}
                  style={{ marginBottom: '1rem' }}
                >
                  <UilTrashAlt
                    style={{
                      width: '24px',
                      height: '24px'
                      // color: '#dc3545'
                    }}
                  />
                </Button>
              </div>
            ))}
          </Tab>
        </Tabs>

        <Button variant="primary" onClick={handleTestRequest}>
          Test Request
        </Button>
      </ModalPopup>

      {/* Response Modal */}
      {showResponse && (
        <Modal
          show={showResponse}
          onHide={() => setShowResponse(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>API Response</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5>Status: {response.status}</h5>
            <pre style={{ maxHeight: '400px', overflow: 'auto' }}>
              {response.text}
            </pre>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResponse(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ApiNodeSidebar;
